/**
 * Formula Builder for Option Entity Fields
 * Generates Excel formulas that reference entity property fields using dot/bracket access
 * (like Excel's built-in Stocks datatype)
 * Wraps formulas in IFERROR to prevent #FIELD! errors and coerce numeric fields
 */

/* global Excel */

/**
 * Check if a field reference needs bracket access (contains spaces or special characters)
 * @param fieldRef - Property name to check (e.g., "Open Interest", "IV")
 * @returns true if bracket access is needed (e.g., "Open Interest" -> true, "IV" -> false)
 */
export function needsBracketAccess(fieldRef: string): boolean {
  // Use bracket access if field contains spaces or non-identifier characters
  // Excel identifiers: letters, numbers, underscore, no spaces
  return /[\s\-()%]/.test(fieldRef);
}

/**
 * Build a field reference string (dot or bracket access)
 * @param entityA1 - A1 notation of the cell containing the entity (e.g., "A1")
 * @param fieldRef - Property key to access (e.g., "IV", "Open Interest", "Updated At")
 * @returns Field reference string (e.g., "A1.IV" or "A1.[Open Interest]")
 */
function buildFieldReference(entityA1: string, fieldRef: string): string {
  if (needsBracketAccess(fieldRef)) {
    // Bracket access for fields with spaces/special chars: =A1.[Open Interest]
    return entityA1 + ".[" + fieldRef + "]";
  } else {
    // Dot access for simple identifiers: =A1.IV
    return entityA1 + "." + fieldRef;
  }
}

/**
 * Build a formula that references an entity field
 * Uses dot/bracket access (like Excel's Stocks datatype) instead of FIELDVALUE
 * Wraps formulas in IFERROR to prevent #FIELD! errors:
 * - Numeric fields: IFERROR(VALUE(...), 0) to coerce to number and return 0 if missing
 * - Datetime fields: IFERROR(..., "") - already numeric (Excel serial), no VALUE() needed
 * - Text fields: IFERROR(..., "") to return empty string if missing
 * 
 * @param entityA1 - A1 notation of the cell containing the entity (e.g., "A1")
 * @param fieldRef - Property key to access (e.g., "IV", "Open Interest", "UpdatedAt")
 * @param fieldType - Field type: "number" for numeric fields, "datetime" for datetime fields, "text" for text fields
 * @returns Excel formula string
 */
export function buildFieldFormula(
  entityA1: string,
  fieldRef: string,
  fieldType: "number" | "datetime" | "text"
): string {
  // Build field reference (dot or bracket access)
  const fieldRefStr = buildFieldReference(entityA1, fieldRef);
  
  // Wrap in IFERROR to prevent #FIELD! errors
  if (fieldType === "number") {
    // Numeric fields: IFERROR(VALUE(...), 0)
    // This ensures:
    // - Missing fields return 0 (not #FIELD!)
    // - Values are coerced to numbers for SUM(), AVERAGE(), etc.
    // - Single-cell math works consistently
    return "=IFERROR(VALUE(" + fieldRefStr + "),0)";
  } else if (fieldType === "datetime") {
    // Datetime fields: IFERROR(..., "")
    // Already numeric (Excel serial), no VALUE() wrapper needed
    // Missing fields return empty string
    return "=IFERROR(" + fieldRefStr + ',"")';
  } else {
    // Text fields: IFERROR(..., "")
    // Missing text fields return empty string
    return "=IFERROR(" + fieldRefStr + ',"")';
  }
}

/**
 * Detect if a formula is an entity property reference
 * Matches patterns like: A1.Delta, B6.[Open Interest], etc.
 * @param formula - Formula string (with or without = prefix)
 * @returns Object with entity cell reference and property name, or null if not an entity reference
 */
export function parseEntityPropertyReference(formula: string): {
  entityRef: string;
  propertyName: string;
} | null {
  // Remove = prefix if present
  const cleaned = formula.trim().replace(/^=/, "");
  
  // Match patterns:
  // - A1.Delta (dot access)
  // - A1.[Open Interest] (bracket access)
  // - Sheet1!A1.Delta (with sheet reference)
  const dotPattern = /^([A-Z]+\d+)(?:\![A-Z]+\d+)?\.([A-Za-z_][A-Za-z0-9_]*)$/;
  const bracketPattern = /^([A-Z]+\d+)(?:\![A-Z]+\d+)?\.\[([^\]]+)\]$/;
  
  const dotMatch = cleaned.match(dotPattern);
  if (dotMatch) {
    return {
      entityRef: dotMatch[1],
      propertyName: dotMatch[2],
    };
  }
  
  const bracketMatch = cleaned.match(bracketPattern);
  if (bracketMatch) {
    return {
      entityRef: bracketMatch[1],
      propertyName: bracketMatch[2],
    };
  }
  
  return null;
}

/**
 * Determine field type from property name
 * Used to decide how to wrap the formula
 * @param propertyName - Property name (e.g., "Delta", "Open Interest", "UpdatedAt")
 * @returns Field type: "number", "datetime", or "text"
 */
function inferFieldType(propertyName: string): "number" | "datetime" | "text" {
  // Datetime fields
  if (propertyName === "UpdatedAt" || propertyName === "UpdatedAtUtc") {
    return "datetime";
  }
  
  // Numeric fields (most entity properties are numeric)
  // This is a heuristic - most option entity fields are numeric
  const numericFields = [
    "Bid", "Ask", "Mid", "Last", "Change", "Change (%)", "Volume",
    "Open Interest", "OI", "Delta", "Gamma", "Theta", "Vega", "Rho", "IV",
    "Strike",
  ];
  
  if (numericFields.includes(propertyName)) {
    return "number";
  }
  
  // Default to text for other fields (Underlying, Right, Expiration, etc.)
  return "text";
}

/**
 * Wrap an entity property reference formula with IFERROR wrapper
 * @param formula - Raw formula (e.g., "B6.[Open Interest]" or "=B6.Delta")
 * @returns Wrapped formula (e.g., "=IFERROR(VALUE(B6.[Open Interest]),0)")
 */
export function wrapEntityPropertyFormula(formula: string): string {
  // Remove = prefix if present
  const cleaned = formula.trim().replace(/^=/, "");
  
  // Check if already wrapped
  if (cleaned.startsWith("IFERROR(")) {
    return "=" + cleaned; // Already wrapped, just add = if missing
  }
  
  // Parse entity property reference
  const parsed = parseEntityPropertyReference(cleaned);
  if (!parsed) {
    // Not an entity property reference, return as-is
    return formula.startsWith("=") ? formula : "=" + formula;
  }
  
  // Determine field type and wrap accordingly
  const fieldType = inferFieldType(parsed.propertyName);
  
  if (fieldType === "number") {
    return "=IFERROR(VALUE(" + cleaned + "),0)";
  } else if (fieldType === "datetime") {
    return "=IFERROR(" + cleaned + ',"")';
  } else {
    return "=IFERROR(" + cleaned + ',"")';
  }
}

/**
 * Wrap selected cell formulas with IFERROR wrapper
 * Useful when users select properties from entity cards
 * @returns Result indicating success/failure
 */
export async function wrapSelectedFormulas(): Promise<{ success: boolean; message: string }> {
  try {
    return await Excel.run(async (context) => {
      const selection = context.workbook.getSelectedRange();
      selection.load(["formulas", "address"]);
      await context.sync();
      
      const formulas = selection.formulas;
      if (!formulas || formulas.length === 0 || !formulas[0] || formulas[0].length === 0) {
        return {
          success: false,
          message: "No formulas found in selection",
        };
      }
      
      let wrappedCount = 0;
      const newFormulas: (string | number)[][] = [];
      
      // Process each cell in the selection
      for (let row = 0; row < formulas.length; row++) {
        const rowFormulas: (string | number)[] = [];
        for (let col = 0; col < formulas[row].length; col++) {
          const cellFormula = formulas[row][col];
          
          if (typeof cellFormula === "string" && cellFormula.trim() !== "") {
            // Check if it's an entity property reference (with or without = prefix)
            const parsed = parseEntityPropertyReference(cellFormula);
            if (parsed) {
              // Check if already wrapped
              const trimmed = cellFormula.trim();
              if (trimmed.startsWith("=IFERROR(") || trimmed.startsWith("IFERROR(")) {
                // Already wrapped, skip
                rowFormulas.push(cellFormula);
              } else {
                // Wrap it
                const wrapped = wrapEntityPropertyFormula(cellFormula);
                rowFormulas.push(wrapped);
                wrappedCount++;
              }
            } else {
              // Not an entity reference, keep as-is
              rowFormulas.push(cellFormula);
            }
          } else {
            // Not a formula, keep as-is
            rowFormulas.push(cellFormula);
          }
        }
        newFormulas.push(rowFormulas);
      }
      
      // Update the selection with wrapped formulas
      selection.formulas = newFormulas;
      await context.sync();
      
      return {
        success: true,
        message: wrappedCount > 0
          ? `Wrapped ${wrappedCount} formula${wrappedCount > 1 ? "s" : ""}`
          : "No entity property references found to wrap",
      };
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      message: "Error: " + errorMessage,
    };
  }
}
